import { getFirebaseToken } from '../firebase';

/**
 * Service for FarmRoad Image Analysis API
 * Handles image upload, AI analysis, history, comparison, and export
 */
class FarmRoadImageService {
  constructor() {
    this.baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';
    this.apiPrefix = '/api/farmroad/images';
  }

  /**
   * Get headers with Firebase authentication token
   */
  async _headers(isMultipart = false) {
    const token = await getFirebaseToken();
    const headers = {
      'Authorization': `Bearer ${token}`
    };

    // Don't set Content-Type for multipart/form-data, browser will set it with boundary
    if (!isMultipart) {
      headers['Content-Type'] = 'application/json';
    }

    return headers;
  }

  /**
   * Upload image with metadata
   * @param {File} file - Image file to upload
   * @param {Object} metadata - Image metadata
   * @returns {Promise<Object>} Upload result with imageId and URLs
   */
  async uploadImage(file, metadata) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('metadata', JSON.stringify({
        farmId: metadata.farmId,
        farmName: metadata.farmName,
        sectorId: metadata.sectorId,
        sectorName: metadata.sectorName,
        compartmentId: metadata.compartmentId,
        compartmentName: metadata.compartmentName,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        uploadedAt: new Date().toISOString(),
        gpsLatitude: metadata.gpsLatitude,
        gpsLongitude: metadata.gpsLongitude,
        notes: metadata.notes || '',
        culture: metadata.culture || 'framboises'
      }));

      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/upload`, {
        method: 'POST',
        headers: await this._headers(true),
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Upload failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  }

  /**
   * Upload image with progress tracking
   * @param {File} file - Image file to upload
   * @param {Object} metadata - Image metadata
   * @param {Function} onProgress - Progress callback (0-100)
   * @returns {Promise<Object>} Upload result
   */
  async uploadImageWithProgress(file, metadata, onProgress) {
    return new Promise(async (resolve, reject) => {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('metadata', JSON.stringify({
          farmId: metadata.farmId,
          farmName: metadata.farmName,
          sectorId: metadata.sectorId,
          sectorName: metadata.sectorName,
          compartmentId: metadata.compartmentId,
          compartmentName: metadata.compartmentName,
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
          uploadedAt: new Date().toISOString(),
          gpsLatitude: metadata.gpsLatitude,
          gpsLongitude: metadata.gpsLongitude,
          notes: metadata.notes || '',
          culture: metadata.culture || 'framboises'
        }));

        const xhr = new XMLHttpRequest();

        // Upload progress
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = Math.round((e.loaded / e.total) * 100);
            onProgress(percentComplete);
          }
        });

        // Request completed
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            reject(new Error(`Upload failed: ${xhr.statusText}`));
          }
        });

        // Request failed
        xhr.addEventListener('error', () => {
          reject(new Error('Network error during upload'));
        });

        // Open and send request
        xhr.open('POST', `${this.baseUrl}${this.apiPrefix}/upload`);

        const token = await getFirebaseToken();
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);

        xhr.send(formData);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Trigger AI analysis on uploaded image
   * @param {string} imageId - Image ID to analyze
   * @param {Object} options - Analysis options
   * @returns {Promise<Object>} Analysis results
   */
  async analyzeImage(imageId, options = {}) {
    try {
      // Construire l'URL avec paramètre advanced si nécessaire
      const url = new URL(`${this.baseUrl}${this.apiPrefix}/analyze/${imageId}`);
      if (options.advanced === true) {
        url.searchParams.append('advanced', 'true');
      }

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: await this._headers(),
        body: JSON.stringify({
          analysisType: options.analysisType || 'comprehensive',
          includeRecommendations: options.includeRecommendations !== false,
          correlateWithSensors: options.correlateWithSensors !== false
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Analysis failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Error analyzing image:', error);
      throw error;
    }
  }

  /**
   * Get image analysis history for a farm
   * @param {string} farmId - Farm ID
   * @param {Object} filters - Optional filters
   * @returns {Promise<Object>} Paginated history
   */
  async getHistory(farmId, filters = {}) {
    try {
      const params = new URLSearchParams({
        farmId,
        ...(filters.sectorId && { sectorId: filters.sectorId }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
        ...(filters.analysisType && { analysisType: filters.analysisType }),
        ...(filters.minHealthScore && { minHealthScore: filters.minHealthScore }),
        ...(filters.maxHealthScore && { maxHealthScore: filters.maxHealthScore }),
        ...(filters.hasIssues !== undefined && { hasIssues: filters.hasIssues }),
        limit: filters.limit || 50,
        offset: filters.offset || 0,
        sortBy: filters.sortBy || 'uploaded_at',
        sortOrder: filters.sortOrder || 'DESC'
      });

      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/history/${farmId}?${params}`, {
        method: 'GET',
        headers: await this._headers()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch history');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching history:', error);
      throw error;
    }
  }

  /**
   * Get detailed analysis for a specific image
   * @param {string} imageId - Image ID
   * @returns {Promise<Object>} Full analysis details
   */
  async getAnalysis(imageId) {
    try {
      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/analysis/${imageId}`, {
        method: 'GET',
        headers: await this._headers()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch analysis');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching analysis:', error);
      throw error;
    }
  }

  /**
   * Compare multiple images temporally
   * @param {Array<string>} imageIds - Array of image IDs to compare
   * @param {Object} options - Comparison options
   * @returns {Promise<Object>} Comparison results
   */
  async compareImages(imageIds, options = {}) {
    try {
      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/compare`, {
        method: 'POST',
        headers: await this._headers(),
        body: JSON.stringify({
          imageIds,
          comparisonType: options.comparisonType || 'temporal',
          includeEvolutionGraphs: options.includeEvolutionGraphs !== false,
          includeSensorCorrelation: options.includeSensorCorrelation !== false
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Comparison failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Error comparing images:', error);
      throw error;
    }
  }

  /**
   * Get images for temporal comparison in a sector
   * @param {string} farmId - Farm ID
   * @param {string} sectorId - Sector ID
   * @param {Object} options - Search options
   * @returns {Promise<Array>} Comparable images
   */
  async getComparableImages(farmId, sectorId, options = {}) {
    try {
      const params = new URLSearchParams({
        farmId,
        sectorId,
        ...(options.gpsLatitude && { gpsLatitude: options.gpsLatitude }),
        ...(options.gpsLongitude && { gpsLongitude: options.gpsLongitude }),
        ...(options.radiusMeters && { radiusMeters: options.radiusMeters }),
        ...(options.startDate && { startDate: options.startDate }),
        ...(options.endDate && { endDate: options.endDate }),
        limit: options.limit || 10
      });

      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/comparable?${params}`, {
        method: 'GET',
        headers: await this._headers()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch comparable images');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching comparable images:', error);
      throw error;
    }
  }

  /**
   * Delete an image and its analysis
   * @param {string} imageId - Image ID to delete
   * @returns {Promise<Object>} Deletion result
   */
  async deleteImage(imageId) {
    try {
      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/image/${imageId}`, {
        method: 'DELETE',
        headers: await this._headers()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Deletion failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  }

  /**
   * Export analysis as PDF
   * @param {string} imageId - Image ID
   * @param {Object} options - Export options
   * @returns {Promise<Blob>} PDF blob
   */
  async exportPDF(imageId, options = {}) {
    try {
      const params = new URLSearchParams({
        includeImage: options.includeImage !== false,
        includeAnnotations: options.includeAnnotations !== false,
        includeSensorData: options.includeSensorData !== false,
        includeRecommendations: options.includeRecommendations !== false,
        includeEvolutionGraphs: options.includeEvolutionGraphs || false,
        language: options.language || 'fr'
      });

      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/export/pdf/${imageId}?${params}`, {
        method: 'GET',
        headers: await this._headers()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'PDF export failed');
      }

      return await response.blob();
    } catch (error) {
      console.error('Error exporting PDF:', error);
      throw error;
    }
  }

  /**
   * Get summary statistics for a farm
   * @param {string} farmId - Farm ID
   * @param {Object} options - Filter options
   * @returns {Promise<Object>} Statistics
   */
  async getStats(farmId, options = {}) {
    try {
      const params = new URLSearchParams({
        farmId,
        ...(options.startDate && { startDate: options.startDate }),
        ...(options.endDate && { endDate: options.endDate }),
        ...(options.sectorId && { sectorId: options.sectorId })
      });

      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/stats/${farmId}?${params}`, {
        method: 'GET',
        headers: await this._headers()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch stats');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching stats:', error);
      throw error;
    }
  }

  /**
   * Get signed URL for an image
   * @param {string} imageId - Image ID
   * @param {number} expiresInHours - URL validity duration (default 24h)
   * @returns {Promise<string>} Signed URL
   */
  async getSignedUrl(imageId, expiresInHours = 24) {
    try {
      const params = new URLSearchParams({ expiresInHours: expiresInHours.toString() });

      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/signed-url/${imageId}?${params}`, {
        method: 'GET',
        headers: await this._headers()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to get signed URL');
      }

      const result = await response.json();
      return result.signedUrl;
    } catch (error) {
      console.error('Error getting signed URL:', error);
      throw error;
    }
  }
}

export default new FarmRoadImageService();
